<div class="flex flex-col w-full h-full bg-white overflow-hidden">
  @if (loading) {
    <app-loader></app-loader>
  }

  <!-- HEADER -->
  <div class="flex justify-between items-start p-6 border-b border-gray-200">
    <div class="flex items-center gap-4 min-w-0">
      <div
        class="w-16 h-16 rounded-2xl bg-gray-200 overflow-hidden flex items-center justify-center shrink-0"
      >
        <span class="text-lg font-semibold text-gray-700">
          {{ employeeInitials }}
        </span>
      </div>

      <div class="flex flex-col min-w-0">
        <span
          class="text-xs text-purple-700 bg-purple-50 px-3 py-1 rounded-full w-fit mb-2"
        >
          Employee Profile
        </span>

        <h2 class="text-xl md:text-2xl font-semibold text-gray-900 truncate">
          {{ employeeDisplayName || modalTitle }}
        </h2>

        <p class="text-sm text-gray-600 truncate">
          {{
            employeeForm.get("email")?.value ||
              "Create and manage employee details, services, and availability."
          }}
        </p>
      </div>
    </div>

    <button
      type="button"
      class="w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-50"
      (click)="closeDialog()"
    >
      ×
    </button>
  </div>

  <!-- KPI CARDS -->
  <div
    class="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-gray-200"
  >
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Employee Status</span>
      <strong class="text-lg font-semibold text-gray-900">
        {{ isEditMode ? "Existing" : "New" }}
      </strong>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Assigned Salon</span>
      <strong class="text-lg font-semibold text-gray-900 truncate block">
        {{ selectedSalonName }}
      </strong>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Services</span>
      <strong class="text-lg font-semibold text-gray-900">
        {{ selectedServiceCount }}
      </strong>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <span class="block text-xs text-gray-600 pb-2">Working Days</span>
      <strong class="text-lg font-semibold text-gray-900">
        {{ enabledWorkingDaysCount }}
      </strong>
    </div>
  </div>

  <!-- TABS -->
  <div class="flex gap-2 overflow-x-auto p-4 border-b border-gray-200 bg-white">
    <button
      *ngFor="let tab of tabs"
      type="button"
      class="px-4 py-2 rounded-3xl border text-sm whitespace-nowrap"
      [class.bg-gray-950]="activeTab === tab.key"
      [class.text-white]="activeTab === tab.key"
      [class.border-gray-950]="activeTab === tab.key"
      [class.bg-white]="activeTab !== tab.key"
      [class.text-gray-700]="activeTab !== tab.key"
      [class.border-gray-200]="activeTab !== tab.key"
      (click)="activeTab = tab.key"
    >
      {{ tab.label }}
    </button>
  </div>

  <!-- BODY -->
  <form
    class="flex-1 overflow-y-auto p-6 flex flex-col gap-6"
    [formGroup]="employeeForm"
    (ngSubmit)="onSubmit()"
  >
    <!-- PROFILE -->
    @if (activeTab === "profile") {
      <section class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-white border border-gray-200 rounded-lg p-5">
          <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
            Basic Information
          </h3>

          <div class="grid gap-4">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                First Name *
              </span>
              <input
                formControlName="firstName"
                type="text"
                class="input"
                placeholder="Enter first name"
                [class.!border-red-500]="!checkValidField('firstName')"
              />
              <span class="error" *ngIf="!checkValidField('firstName')">
                First Name is required
              </span>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Last Name *
              </span>
              <input
                formControlName="lastName"
                type="text"
                class="input"
                placeholder="Enter last name"
                [class.!border-red-500]="!checkValidField('lastName')"
              />
              <span class="error" *ngIf="!checkValidField('lastName')">
                Last Name is required
              </span>
            </label>

            <div>
              <app-glo-date-picker
                [formControlInput]="fetchInputField('dateOfBirth')"
                [label]="'Date of Birth *'"
                [placeholder]="'Choose DOB'"
                [classes]="inputClasses"
                [isRequired]="!checkValidField('dateOfBirth')"
              ></app-glo-date-picker>
            </div>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">Gender *</span>
              <select
                formControlName="gender"
                class="input"
                [class.!border-red-500]="!checkValidField('gender')"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              <span class="error" *ngIf="!checkValidField('gender')">
                Gender is required
              </span>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Marital Status *
              </span>
              <select
                formControlName="maritalStatus"
                class="input"
                [class.!border-red-500]="!checkValidField('maritalStatus')"
              >
                <option value="">Select Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
              <span class="error" *ngIf="!checkValidField('maritalStatus')">
                Marital Status is required
              </span>
            </label>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg p-5">
          <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
            Contact Information
          </h3>

          <div class="grid gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>

              <ngx-intl-tel-input
                class="block w-full"
                [cssClass]="customCssClass"
                [preferredCountries]="preferredCountries"
                [enableAutoCountrySelect]="true"
                [enablePlaceholder]="true"
                [searchCountryFlag]="true"
                [searchCountryField]="[
                  SearchCountryField.Iso2,
                  SearchCountryField.Name,
                ]"
                [selectFirstCountry]="false"
                [selectedCountryISO]="CountryISO.UnitedStates"
                [maxLength]="15"
                [phoneValidation]="true"
                [separateDialCode]="separateDialCode"
                [numberFormat]="PhoneNumberFormat.National"
                name="phone_number"
                formControlName="phone_number"
              ></ngx-intl-tel-input>

              <small *ngIf="validationChecker()" class="text-red-500">
                Invalid Phone Number
              </small>
            </div>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">Email *</span>
              <input
                formControlName="email"
                type="email"
                class="input"
                placeholder="Enter email address"
                [class.!border-red-500]="!checkValidField('email')"
              />
              <span class="error" *ngIf="!checkValidField('email')">
                Email is required
              </span>
            </label>

            @if (!isEditMode) {
              <label class="flex flex-col gap-1 relative">
                <span class="text-sm font-medium text-gray-700">
                  Password *
                </span>

                <div class="relative">
                  <input
                    [type]="passwordFieldType"
                    autocomplete="new-password"
                    formControlName="password"
                    class="input pr-10"
                    placeholder="Enter password"
                    [class.!border-red-500]="!checkValidField('password')"
                  />

                  <button
                    type="button"
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                    (click)="togglePasswordVisibility()"
                  >
                    {{ passwordFieldType === "password" ? "Show" : "Hide" }}
                  </button>
                </div>

                <span class="error" *ngIf="!checkValidField('password')">
                  Password is required, minimum 8 characters.
                </span>
              </label>
            }
          </div>
        </div>
      </section>
    }

    <!-- ADDRESS -->
    @if (activeTab === "address") {
      <section class="bg-white border border-gray-200 rounded-lg p-5">
        <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
          Address Information
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label class="flex flex-col gap-1 md:col-span-3">
            <span class="text-sm font-medium text-gray-700">Address *</span>
            <input
              formControlName="address"
              type="text"
              class="input"
              placeholder="Enter street address"
              [class.!border-red-500]="!checkValidField('address')"
            />
            <span class="error" *ngIf="!checkValidField('address')">
              Address is required
            </span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-gray-700">City *</span>
            <input
              formControlName="addressCity"
              type="text"
              class="input"
              placeholder="Enter city"
              [class.!border-red-500]="!checkValidField('addressCity')"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-gray-700">
              Province/State *
            </span>
            <input
              formControlName="province"
              type="text"
              class="input"
              placeholder="Enter province/state"
              [class.!border-red-500]="!checkValidField('province')"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-gray-700">
              Postal Code *
            </span>
            <input
              formControlName="postalCode"
              type="text"
              class="input"
              placeholder="Enter postal code"
              [class.!border-red-500]="!checkValidField('postalCode')"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-gray-700">
              Nationality *
            </span>
            <select
              formControlName="nationality"
              class="input"
              [class.!border-red-500]="!checkValidField('nationality')"
            >
              <option value="">Select Country</option>
              <option value="usa">USA</option>
              <option value="canada">Canada</option>
              <option value="uk">UK</option>
              <option value="india">India</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-gray-700">
              Profile City *
            </span>
            <input
              formControlName="city"
              type="text"
              class="input"
              placeholder="Enter profile city"
              [class.!border-red-500]="!checkValidField('city')"
            />
          </label>
        </div>
      </section>
    }

    <!-- EMPLOYMENT -->
    @if (activeTab === "employment") {
      <section class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-white border border-gray-200 rounded-lg p-5">
          <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
            Joining Details
          </h3>

          <div class="grid gap-4">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Job Title *
              </span>
              <input
                formControlName="job_title"
                type="text"
                class="input"
                placeholder="Enter job title"
                [class.!border-red-500]="!checkValidField('job_title')"
              />
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">Salon *</span>
              <select
                formControlName="salon_id"
                class="input"
                [class.!border-red-500]="!checkValidField('salon_id')"
              >
                <option value="">Choose Salon</option>
                @for (salon of salons; track salon.salon_id) {
                  <option [value]="salon.salon_id">
                    {{ salon.name }}
                  </option>
                }
              </select>
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Department *
              </span>
              <input
                formControlName="departmentName"
                type="text"
                class="input"
                placeholder="Enter department name"
                [class.!border-red-500]="!checkValidField('departmentName')"
              />
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Designation *
              </span>
              <input
                formControlName="designation"
                type="text"
                class="input"
                placeholder="Enter designation"
                [class.!border-red-500]="!checkValidField('designation')"
              />
            </label>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-lg p-5">
          <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
            Compensation & Reporting
          </h3>

          <div class="grid gap-4">
            <div>
              <app-glo-date-picker
                [formControlInput]="fetchInputField('interviewDate')"
                [label]="'Interview Date *'"
                [placeholder]="'Select date'"
                [classes]="inputClasses"
                [isRequired]="!checkValidField('interviewDate')"
              ></app-glo-date-picker>
            </div>

            <div>
              <app-glo-date-picker
                [formControlInput]="fetchInputField('hiringDate')"
                [label]="'Hiring Date *'"
                [placeholder]="'Select date'"
                [classes]="inputClasses"
                [isRequired]="!checkValidField('hiringDate')"
              ></app-glo-date-picker>
            </div>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Gross Salary *
              </span>
              <input
                formControlName="grossSalary"
                type="number"
                class="input"
                placeholder="Enter salary"
                [class.!border-red-500]="!checkValidField('grossSalary')"
              />
            </label>

            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">
                Reporting To *
              </span>
              <input
                formControlName="reportingTo"
                type="text"
                class="input"
                placeholder="Enter manager name"
                [class.!border-red-500]="!checkValidField('reportingTo')"
              />
            </label>
          </div>
        </div>
      </section>
    }

    <!-- SERVICES -->
    @if (activeTab === "services") {
      <section class="bg-white border border-gray-200 rounded-lg p-5">
        <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
          Services Employee Can Perform
        </h3>

        <div
          *ngIf="!employeeForm.get('salon_id')?.value"
          class="text-sm text-gray-500"
        >
          Select a salon first to load services.
        </div>

        <div
          *ngIf="employeeForm.get('salon_id')?.value && loadingServices"
          class="text-sm text-gray-500"
        >
          Loading services...
        </div>

        <div
          *ngIf="employeeForm.get('salon_id')?.value && !loadingServices"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <label
            *ngFor="let service of services"
            class="service-card"
            [class.service-card-active]="isServiceSelected(service.service_id)"
          >
            <input
              type="checkbox"
              class="mt-1"
              [checked]="isServiceSelected(service.service_id)"
              (change)="
                onServiceToggle(service.service_id, $any($event.target).checked)
              "
            />

            <div class="flex-1 min-w-0">
              <div class="font-medium text-sm text-gray-900">
                {{ service.name }}
              </div>

              <div class="text-xs text-gray-500">
                ${{ service.price }} · {{ service.duration_minutes || 30 }} min
              </div>
            </div>
          </label>
        </div>

        <div
          *ngIf="
            employeeForm.get('selected_service_ids')?.touched &&
            employeeForm.get('selected_service_ids')?.invalid
          "
          class="text-red-500 text-sm mt-2"
        >
          Select at least one service.
        </div>
      </section>
    }

    <!-- AVAILABILITY -->
    @if (activeTab === "availability") {
      <section class="bg-white border border-gray-200 rounded-lg p-5">
        <h3 class="text-sm md:text-lg text-gray-900 font-medium pb-4">
          Weekly Availability
        </h3>

        <div formArrayName="working_hours" class="flex flex-col gap-3">
          <div
            *ngFor="let row of workingHoursArray.controls; let i = index"
            [formGroupName]="i"
            class="grid grid-cols-1 md:grid-cols-[140px_120px_1fr_1fr] gap-3 items-center border border-gray-200 rounded-xl p-4"
          >
            <div class="font-medium text-gray-900">
              {{ row.get("day_label")?.value }}
            </div>

            <label class="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" formControlName="enabled" />
              <span>Working</span>
            </label>

            <div>
              <label class="block text-xs text-gray-500 mb-1">Start</label>
              <input type="time" formControlName="start_time" class="input" />
            </div>

            <div>
              <label class="block text-xs text-gray-500 mb-1">End</label>
              <input type="time" formControlName="end_time" class="input" />
            </div>
          </div>
        </div>

        <div class="text-xs text-gray-500 mt-3">
          Days not marked as working will be saved as off days.
        </div>
      </section>
    }

    <!-- FOOTER -->
    <div class="sticky bottom-0 bg-white pt-4 border-t border-gray-200">
      <div class="ml-auto flex justify-end gap-3 flex-wrap">
        <button
          type="button"
          class="px-6 py-2 shadow rounded-3xl bg-white text-gray-900 border border-gray-200"
          (click)="onReset()"
        >
          Reset
        </button>

        <button
          type="button"
          class="px-6 py-2 shadow rounded-3xl bg-white text-gray-900 border border-gray-200"
          (click)="closeDialog()"
        >
          Cancel
        </button>

        <button
          type="submit"
          class="px-6 py-2 shadow rounded-3xl bg-gray-950 text-white"
        >
          {{ isEditMode ? "Update Employee" : "Add Employee" }}
        </button>
      </div>
    </div>
  </form>
</div>
